import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, CheckCircle2, Zap, Users, Factory, TrendingUp, Leaf, Globe } from 'lucide-react';
import { useInView } from '../components/UI';

const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const thesisPoints = [
  { title: 'Environmental return', desc: 'Measurable CO₂ reductions eligible for Gold Standard and Verra registration. Carbon credits generate direct revenue alongside fleet operations.' },
  { title: 'Social return', desc: 'Employment at every level: riders, station staff, technicians, engineers, and EV Academy graduates. 500K+ direct opportunities by 2045.' },
  { title: 'Industrial return', desc: 'Sankofa Foundry builds lasting manufacturing capacity, keeping value, IP, skills, and employment in Africa rather than exporting them.' },
  { title: 'Financial return', desc: 'All three impact dimensions are also revenue drivers. Carbon finance, employment scale, and manufacturing margin compound the core fleet and BaaS revenues.' },
];

const dimensions = [
  { stat: '~1.5T', statLabel: 'CO₂ saved per bike per year', title: 'CO₂ Emission Reductions', icon: Leaf, desc: 'Every electric motorcycle we put on the road replaces a petrol-powered one. With 100,000 vehicles on the road, Azura\'s fleet generates measurable annual emissions reductions eligible for carbon credit registration.', img: '/images/impact4.jpg' },
  { stat: '10K+', statLabel: 'Daily charging cycles at scale', title: 'Centralized Clean Charging', icon: Zap, desc: 'Battery swapping centralizes charging, making energy use more efficient and paving the way for renewable energy sources at swap stations.', img: '/images/bg1.jpg' },
  { stat: '500K+', statLabel: 'Direct employment by 2045', title: 'Employment Creation', icon: Users, desc: 'Riders, station staff, technicians, EV Academy graduates, engineers, and support personnel — providing employment at every level of the ecosystem and at every phase of growth.', img: '/images/impact3.webp' },
  { stat: '70%+', statLabel: 'Potential fuel cost reduction', title: 'Income Enhancement', icon: TrendingUp, desc: 'Reduced operating costs significantly increase take-home pay for commercial motorcycle operators, converting the 30–50% fuel burden into a fraction of previous operating costs.', img: '/images/impact5.jpg' },
  { stat: '$6', statLabel: 'Validated daily operator cost', title: 'Transportation Affordability', icon: Globe, desc: 'Lower fleet operating costs translate to more affordable fares for commuters and lower delivery costs for businesses, distributing the economic benefit of electrification across the urban economy.', img: '/images/bike6.png' },
  { stat: '2042', statLabel: "Africa's first local EV manufacturer", title: 'Industrial Development', icon: Factory, desc: 'The Sankofa Foundry roadmap creates lasting manufacturing capacity, keeping value, IP, technical skills, and employment in Africa. By 2042, Azura is the first African company to own the full design and production of its electric vehicles.', img: '/images/impact2.jpg' },
];

const carbonTags = ['Gold Standard', 'Verra VCS', 'DFI Eligible', 'ESG Mandates', 'Blended Finance'];

const capitalMechanisms = [
  { title: 'DFI Funding', desc: 'Development Finance Institution funding windows tied to climate and development mandates.' },
  { title: 'Carbon Markets', desc: 'Direct revenue from verified emissions reductions registered under Gold Standard or Verra.' },
  { title: 'ESG Investment', desc: 'Institutional capital aligned to sustainability outcomes: ESG funds and mandated impact allocations.' },
  { title: 'Climate Grants', desc: 'Low-cost funding for infrastructure with measurable development and climate impact.' },
  { title: 'Sustainability-Linked Debt', desc: 'Debt instruments priced against ESG performance metrics, with a lower cost of capital as impact is verified.' },
];

const sdgs = [
  { num: '7', title: 'Clean Energy', desc: 'Battery swapping and EV operations reduce dependence on fossil fuels and make clean energy easier to manage across city transportation networks.' },
  { num: '8', title: 'Decent Work', desc: 'Creating employment for riders, station staff, technicians, EV Academy graduates, and engineers at every level and phase of growth.' },
  { num: '9', title: 'Industry & Innovation', desc: 'The Sankofa Foundry roadmap builds lasting manufacturing capacity and engineering expertise in Africa, from local vehicle assembly to full local manufacturing.' },
  { num: '11', title: 'Sustainable Cities', desc: 'Cleaner urban transportation reduces air pollution in Ibadan, Lagos, Abuja, Kano, and eventually cities across five African markets.' },
  { num: '13', title: 'Climate Action', desc: 'Measurable CO₂ reductions from every EV we put on the road, eligible for Gold Standard and Verra registration, generating verifiable climate impact at scale.' },
];

const tiers = [
  { stat: '8M+', tier: 'Tier 1', title: 'Commercial Riders', desc: 'Motorcycle operators transitioning from petrol to electric, with immediate income improvement of up to 70% through reduced fuel costs.' },
  { stat: '10K+', tier: 'Tier 2', title: 'Station Operations', desc: 'BaaS swap station staff, battery technicians, station managers, and maintenance personnel at each of the 3,334+ stations by 2036.' },
  { stat: '2,000+', tier: 'Tier 3', title: 'Engineering & Technology', desc: 'EV Academy graduates, software engineers, fleet technology specialists, and data analysts powering the platform layer.' },
  { stat: '5,000+', tier: 'Tier 4', title: 'Sankofa Foundry', desc: 'Assembly workers, industrial engineers, quality assurance, supply chain, and R&D staff as the manufacturing roadmap unfolds from 2033.' },
];

const Img: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => (
  <img src={src} alt={alt} className={className}
    onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = '#E5E3DC'; }} />
);

/* ══ IMPACT PAGE ══ */
const Impact: React.FC = () => {
  const { ref: thesisRef, inView: thesisInView } = useInView();
  const { ref: dimRef, inView: dimInView } = useInView();
  const { ref: carbonRef, inView: carbonInView } = useInView();
  const { ref: sdgRef, inView: sdgInView } = useInView();
  const { ref: tierRef, inView: tierInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/70">/</span>
            <span className="text-accent-amber text-xs font-semibold">Impact</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
            Environmental & Social <span className="text-accent-amber">Impact.</span>
          </h1>

          <p className="max-w-2xl text-white/70 text-base leading-8 mb-10">Every vehicle we put on the road reduces emissions,
           every swap station creates employment, and every phase of the manufacturing roadmap builds lasting industrial know-how in Africa.
          </p>

        </div>
      </section>

      {/* ══ THE IMPACT THESIS ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={thesisRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-dark border border-border group">
                <Img src="/images/impact.jpg" alt="Impact thesis" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
        
              </div>
            </motion.div>

            <div className={`lg:col-span-7 transition-all duration-700 ${thesisInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Impact Thesis</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-5">
                Growth and sustainability<br />
                <span className="text-accent-amber italic">are the same thing here.</span>
              </h2>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-5">
                Most impact investments require a trade-off: lower returns for positive outcomes. Azura does not offer that trade-off. The business model and the impact case are structurally identical.
              </p>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-8">
                Every electric motorcycle we put on the road replaces a petrol-powered one, reducing emissions, lowering rider costs, and generating carbon credit revenue. Every swap station creates employment. Every phase of Sankofa Foundry builds manufacturing capacity that Africa has never had. This is not ESG compliance. It is the mechanism by which Azura creates and earns value.
              </p>

              <div className="space-y-3">
                {thesisPoints.map((p, i) => (
                  <div key={i}
                    className={`flex items-start gap-3.5 p-4 rounded-2xl bg-white border border-border shadow-card hover:border-bg-dark/25 transition-all duration-200 ${thesisInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${i * 70}ms` }}>
                    <div className="w-5 h-5 rounded-full bg-accent-amber-light border border-accent-amber/30 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle2 size={11} className="text-bg-dark" />
                    </div>
                    <div>
                      <p className="font-figtree font-bold text-sm text-bg-dark mb-0.5">{p.title}</p>
                      <p className="font-exo text-xs text-neutral-500 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SIX IMPACT DIMENSIONS ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={dimRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${dimInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Six Impact Dimensions</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              From carbon reduction<br />
              <span className="text-accent-amber italic">to continental industrialisation.</span>
            </h2>
          </div>

          <div className="space-y-8">
            {dimensions.map((d, i) => {
              const Icon = d.icon;
              const isEven = i % 2 === 0;
              return (
                <div key={d.title}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center transition-all duration-700 ${dimInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}>

                  <div className={`lg:col-span-4 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-card border border-border group">
                      <Img src={d.img} alt={d.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
                     
                      <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-bg-dark/80 backdrop-blur border border-white/10 flex items-center justify-center text-accent-amber">
                        <Icon size={18} />
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-8 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="bg-white border border-border rounded-2xl p-8 shadow-card relative overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                      <span className="absolute bottom-5 right-6 font-figtree font-black text-[5rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-neutral-50 border border-border flex items-center justify-center text-bg-dark">
                          <Icon size={18} />
                        </div>
                        <h3 className="font-figtree font-black text-xl text-bg-dark tracking-tight">{d.title}</h3>
                      </div>
                      <p className="font-exo text-sm text-neutral-600 leading-relaxed">{d.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ CARBON FINANCE ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white relative overflow-hidden border-t-4 border-accent-amber">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div ref={carbonRef} className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-14">
            <div className={`lg:col-span-6 transition-all duration-700 ${carbonInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Carbon Finance</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5">
                Eligibility by design.<br />
                <span className="text-accent-amber italic">Revenue from day one.</span>
              </h2>
              <p className="font-exo text-white/55 text-sm leading-relaxed mb-5">
                Azura's operations are designed from inception to qualify for Gold Standard and Verra VCS carbon credit frameworks — the two most credible international carbon registries. The company begins the registration process at launch, given that qualification timelines are long and early movers earn more value.
              </p>
              <p className="font-exo text-white/70 text-sm leading-relaxed mb-6">
                Carbon credits are not a secondary consideration. They are a direct revenue stream that compounds with fleet size: every additional vehicle we put on the road adds to the annual credits generated and registered.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {carbonTags.map(t => (
                  <span key={t} className="px-3 py-1.5 rounded-full border border-accent-amber/30 bg-accent-amber/10 text-xs font-bold uppercase tracking-widest text-accent-amber">{t}</span>
                ))}
              </div>

              {/* Registration strategy note */}
              <div className="p-5 rounded-2xl bg-white/[0.04] border border-white/[0.08]">
                <p className="text-xs text-accent-amber uppercase tracking-widest mb-2">Registration Strategy</p>
                <p className="font-exo text-xs text-white/70 leading-relaxed">
                  Azura begins the registration process at launch, not at scale. Qualification timelines under Gold Standard can run 12–24 months. Early registration means credits begin accruing from the first year of operation, not years later.
                </p>
              </div>
            </div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden aspect-video shadow-dark border border-white/10 group mb-5">
                <Img src="/images/bike5.png" alt="Carbon finance" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-br from-bg-dark/70 to-accent-amber/10" />
                <div className="absolute inset-0 flex items-center justify-center text-center px-6">
              
                </div>
              </div>

              {/* Capital access mechanisms */}
              <div className="space-y-3">
                {capitalMechanisms.map((m, i) => (
                  <div key={m.title}
                    className={`flex gap-4 p-4 rounded-xl bg-white/[0.04] border border-white/[0.08] transition-all duration-700 ${carbonInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                    style={{ transitionDelay: `${i * 70}ms` }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-amber shrink-0 mt-1.5" />
                    <div>
                      <p className="font-figtree font-bold text-xs text-white mb-0.5">{m.title}</p>
                      <p className="font-exo text-xs text-white/70 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ UN SDGs ══ */}
      <section className="py-20 px-6 bg-white border-t border-border">
        <div ref={sdgRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${sdgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">UN Sustainable Development Goals</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-3">
              Five SDGs. Aligned by design,<br />
              <span className="text-accent-amber italic">not by declaration.</span>
            </h2>
            <p className="font-exo text-neutral-600 text-sm leading-relaxed max-w-2xl">
              Azura's activities directly advance five UN SDGs, not through peripheral programmes, but through the core operations of the business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sdgs.map((s, i) => (
              <div key={s.num}
                className={`bg-white border border-border rounded-2xl p-7 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden ${sdgInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-bg-dark flex items-center justify-center font-figtree font-black text-accent-amber text-lg shrink-0 group-hover:bg-accent-amber group-hover:text-bg-dark transition-all duration-300">
                    {s.num}
                  </div>
                  <h3 className="font-figtree font-black text-base text-bg-dark leading-snug">{s.title}</h3>
                </div>
                <p className="font-exo text-xs text-neutral-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ EMPLOYMENT & LIVELIHOODS ══ */}
      <section className="py-20 px-6 bg-bg-cream border-t border-border">
        <div ref={tierRef} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            <div className={`lg:col-span-5 transition-all duration-700 ${tierInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Employment & Livelihoods</span>
              </div>
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-5">
                Not just jobs.<br />
                <span className="text-accent-amber italic">Careers in Africa's industrial future.</span>
              </h2>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-4">
                Azura creates employment at every tier of the ecosystem, from the rider on the street to the engineer in the Sankofa Foundry. The EV Academy ensures that local talent is trained and ready at each phase of the manufacturing roadmap.
              </p>
              <p className="font-exo text-neutral-600 text-sm leading-relaxed mb-8">
                When a motorcycle operator switches from petrol to Azura's electric fleet, their take-home income increases by up to 70%. That income improvement supports families, drives local spending, and compounds across the 8 million+ commercial motorcycle operators in Nigeria alone.
              </p>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft}>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-card border border-border group">
                  <Img src="/images/impact6.jpg" alt="Employment" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <p className="font-figtree font-black text-3xl text-accent-amber">500K+</p>
                    <p className="text-xs text-white/70 uppercase tracking-widest">Direct opportunities by 2045</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {tiers.map((t, i) => (
                <div key={t.tier}
                  className={`bg-white border border-border rounded-2xl p-7 shadow-card relative overflow-hidden hover:shadow-card-hover transition-all duration-300 ${tierInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}>
                  <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-xs text-accent-amber uppercase tracking-widest mb-1">{t.tier}</p>
                      <h3 className="font-figtree font-black text-lg text-bg-dark">{t.title}</h3>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-figtree font-black text-3xl text-accent-amber tracking-tight">{t.stat}</p>
                    </div>
                  </div>
                  <p className="font-exo text-xs text-neutral-500 leading-relaxed">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CLOSING STATEMENT + CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber relative overflow-hidden">
      
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <span className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-5">The Proof of Concept</span>
          <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-6 leading-tight">
            Africa can grow without<br />
            <span className="text-accent-amber italic">repeating the past.</span>
          </h2>
          <p className="font-exo text-white/55 text-base leading-relaxed mb-10 max-w-2xl mx-auto">
            Every continent that industrialised before Africa did so by burning fossil fuels and exporting the environmental cost to future generations. Africa has the opportunity (and now the capital, the policy support, and the technology) to do it differently. Azura is one company. But it is also a demonstration. A proof that the trade-off between growth and sustainability is a false choice.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/opportunity"
              className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all ">
              View The Opportunity <ArrowRight size={13} />
            </Link>
            <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
              Download White Paper ↗
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Impact;